import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'rebass/styled-components';
import { faStripe } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import { useDispatch } from 'react-redux';
import { faX } from '@fortawesome/pro-solid-svg-icons';
import { apiAction } from 'shared/actions/api';

import { Box, Flex, Text } from 'shared/components/display';

import { STRIPE_COPY } from 'shared/config/copy';
import { useStripe } from 'shared/hooks/useStripe';
import { stripeLogo } from 'shared/images/stripe';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import COLORS from 'shared/styles/colors';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { BrkfstUser } from 'shared/typings/user';

import ExternalLink from 'shared/components/atoms/ExternalLink/ExternalLink';
import styles from './styles';
import { RESOURCES } from 'shared/config/resourceNames';
import { useComponentLoading } from 'shared/hooks/useComponentLoading';

export enum StripeConnectVariant {
  PRIMARY = 'primary',
  SECONDARY = 'seconday',
}
interface Props {
  user: Pick<BrkfstUser, 'id' | 'email' | 'stripeAccountId'>;
  redirectURI: string;
  className?: string;
  variant?: StripeConnectVariant;
  onboarding?: boolean;
}

interface StripeState {
  userId: number;
  redirectURI: string;
}

const StripeConnect: React.FC<Props> = ({
  user,
  redirectURI,
  onboarding = false,
  className,
  variant = StripeConnectVariant.PRIMARY,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { fetchExpressDashboardLink } = useStripe();
  const [stripeDashboardUrl, setStripeDashboardUrl] = useState('');
  const { id: userId, email, stripeAccountId } = user;

  const state = useMemo(() => {
    const data: StripeState = { userId, redirectURI };
    return JSON.stringify(data);
  }, [userId, redirectURI]);

  useEffect(() => {
    const setDashboardUrl = ({ data }) => {
      const { url } = data;
      setStripeDashboardUrl(url);
    };

    const onError = () => {
      dispatch(
        apiAction({
          path: {
            route: '/user/stripe/reconnect',
          },
          method: 'POST',
          data: {
            onboarding,
          },
          entity: RESOURCES.STRIPE,
          onSuccess: ({ data }) => {
            window.location.assign(data.url);
          },
        }),
      );
    };

    if (stripeAccountId) fetchExpressDashboardLink(setDashboardUrl, onError);
  }, [stripeAccountId]);

  const onConnectClick = (e) => {
    e.stopPropagation();
    setLoading(true);
    dispatch(
      apiAction({
        path: {
          route: '/user/stripe/connect',
        },
        method: 'POST',
        data: {
          onboarding,
        },
        entity: RESOURCES.STRIPE,
        onSuccess: ({ data }) => {
          window.location.assign(data.url);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  return (
    <Flex className={cs('stripe-connect', className)} css={styles}>
      {stripeAccountId ? (
        <>
          <Box className="stripe-connect__connected-wrapper">
            <a
              className={
                variant === StripeConnectVariant.PRIMARY
                  ? '&__secondary-wrapper-start'
                  : `stripe-connect__secondary-wrapper`
              }
              href={stripeDashboardUrl}
              tabIndex={0}
              target="_blank"
            >
              {variant === StripeConnectVariant.SECONDARY ? (
                <Button variant={BUTTON_VARIANTS.TEXT} className={'stripe-connect__connect-button'}>
                  Access
                </Button>
              ) : (
                <Text className="stripe-connect__connect-button-start" variant={TEXT_VARIANTS.SUBHEADING_SM}>
                  Access
                </Text>
              )}
              <img src={stripeLogo} alt={STRIPE_COPY.ALT_LOGO} height="25rem" className="stripe-connect__stripe-logo" />
            </a>
            <Text
              variant={TEXT_VARIANTS.CATEGORY}
              className={
                variant === StripeConnectVariant.PRIMARY
                  ? 'stripe-connect__connected-message'
                  : 'stripe-connect__connected-message--end'
              }
            >
              Connected
            </Text>
            <Flex className="stripe-connect__disconnect-wrapper">
              <FontAwesomeIcon className="stripe-connect__disconnect-icon" icon={faX} color={COLORS.NEUTRAL400} />
              <ExternalLink
                className="stripe-connect__disconnect"
                text="Disconnect"
                href="https://dashboard.stripe.com/settings/apps"
              />
            </Flex>
          </Box>
        </>
      ) : variant === StripeConnectVariant.PRIMARY ? (
        <Box className="stripe-connect__connect-wrapper">
          <Button disabled={loading} onClick={onConnectClick} className="stripe-connect__link">
            <Text variant={TEXT_VARIANTS.H6} className="stripe-connect__link__text">
              Connect with
            </Text>
            <FontAwesomeIcon icon={faStripe} className="stripe-connect__link__logo" size="2x" color={COLORS.WHITE} />
          </Button>
          <Text variant={TEXT_VARIANTS.CAPTION} className="stripe-connect__country-hint">
            {STRIPE_COPY.CONNECT_COUNTRY_HINT}
          </Text>
        </Box>
      ) : (
        <span className="stripe-connect__secondary-wrapper">
          <Button
            disabled={loading}
            onClick={onConnectClick}
            variant={BUTTON_VARIANTS.TEXT}
            className="stripe-connect__connect-button"
          >
            Connect
          </Button>
          <img src={stripeLogo} alt={STRIPE_COPY.ALT_LOGO} height="25rem" className="stripe-connect__stripe-logo" />
        </span>
      )}
    </Flex>
  );
};

export default StripeConnect;
